import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { Card } from '../../../layout';
import { bytesToSize } from '../../../../utils';
import { FormatIcon, FormatIconSize, FormatIconType } from '../../FormatIcon';
import Loader from '../../Loader';
import SvgIcon from '../../SvgIcon';

import useStyles from './styles';
import { IFileCardListItem } from './types';

const NO_THUMBNAIL_URL = 'https://cdn140.picsart.com/27501421275989682149.svg';

function FileCardListItem({
  className,
  disabled,
  deleteDisabled,
  fileName,
  fileSize,
  format,
  isLoading,
  generateThumbnail,
  headerLeft,
  headerCenter,
  headerRight,
  footerLeft,
  footerCenter,
  footerRight,
  onDelete,
  onPreview,
  previewDisabled,
  thumbnail,
}: IFileCardListItem) {
  const classes = useStyles();

  const [displayedThumbnail, setDisplayedThumbnail] = useState(thumbnail);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);

  useEffect(() => {
    setDisplayedThumbnail(thumbnail);
  }, [thumbnail]);

  const headerLeftContent = useMemo(
    () => (typeof headerLeft === 'function' ? headerLeft({ disabled }) : headerLeft),
    [disabled, headerLeft],
  );
  const headerCenterContent = useMemo(
    () => (typeof headerCenter === 'function' ? headerCenter({ disabled }) : headerCenter),
    [disabled, headerCenter],
  );
  const headerRightContent = useMemo(
    () => (typeof headerRight === 'function' ? headerRight({ disabled }) : headerRight),
    [disabled, headerRight],
  );
  const footerLeftContent = useMemo(
    () => (typeof footerLeft === 'function' ? footerLeft({ disabled }) : footerLeft),
    [disabled, footerLeft],
  );
  const footerCenterContent = useMemo(
    () => (typeof footerCenter === 'function' ? footerCenter({ disabled }) : footerCenter),
    [disabled, footerCenter],
  );
  const footerRightContent = useMemo(
    () => (typeof footerRight === 'function' ? footerRight({ disabled }) : footerRight),
    [disabled, footerRight],
  );

  const thumbnailContent = useMemo(() => {
    if (generatingThumbnail || isLoading) {
      return <Loader />;
    }

    if (displayedThumbnail?.text) {
      return <span className={classes.thumbnailText}>{displayedThumbnail.text}</span>;
    }

    return (
      <LazyLoadImage
        className={classes.thumbnail}
        src={displayedThumbnail?.url ?? NO_THUMBNAIL_URL}
        alt="Thumbnail of file type or file contents"
      />
    );
  }, [displayedThumbnail, generatingThumbnail, isLoading]);

  useEffect(() => {
    if (displayedThumbnail == null && generateThumbnail != null) {
      setGeneratingThumbnail(true);

      async function generate() {
        const generated = await generateThumbnail!();
        setDisplayedThumbnail(generated);
        setGeneratingThumbnail(false);
      }

      generate();
    }
  }, [generateThumbnail, displayedThumbnail]);

  return (
    <Card className={classNames(classes.root, className)}>
      <div className={classes.header}>
        <div className={classes.areaLeft}>
          {headerLeftContent}
          <FormatIcon format={format ?? FormatIconType.GENERIC} size={FormatIconSize.SMALL} />
        </div>
        <div className={classes.areaCenter}>{headerCenterContent}</div>
        <div className={classNames(classes.areaRight, classes.actions)}>
          {onPreview && (
            <button className="preview" disabled={disabled || previewDisabled} onClick={onPreview}>
              <SvgIcon name="picsart-main-icons-preview" />
            </button>
          )}
          {onDelete && (
            <button className="delete" disabled={disabled || deleteDisabled} onClick={onDelete}>
              <SvgIcon name="picsart-main-icons-delete" />
            </button>
          )}
          {headerRightContent}
        </div>
      </div>
      <div className={classes.content}>
        <div
          className={classNames(classes.page, {
            landscape: displayedThumbnail?.orientation === 'landscape',
            'margin-small': displayedThumbnail?.margin === 'small',
            'margin-large': displayedThumbnail?.margin === 'large',
          })}
        >
          <SvgIcon className={classNames(classes.corner)} name="file-corner-gen" />
          {thumbnailContent}
        </div>
      </div>
      <div className={classes.footer}>
        {footerLeftContent && <div className={classes.areaLeft}>{footerLeftContent}</div>}
        <div className={classes.areaCenter}>
          <div className={classes.fileNameSizeContainer}>
            {fileName && (
              <div className={classes.fileName} title={fileName}>
                {fileName}
              </div>
            )}
            {fileSize && <div className={classes.fileSize}>{bytesToSize(fileSize)}</div>}
          </div>
          {footerCenterContent}
        </div>
        {footerRightContent && <div className={classes.areaRight}>{footerRightContent}</div>}
      </div>
    </Card>
  );
}

export default FileCardListItem;
